import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _isEqual from 'lodash/isEqual';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Page, FocusDiv } from '@accedo/vdkweb-tv-ui';
import TabView from '#/components/TabView/TabView';
import { PlaylistSwimlaneFactory } from '#/components/SwimlaneFactory';
import { onVideoTileClick } from '#/components/Swimlane/utils';
import { getVerticalNav, PLAYER } from '#/utils/navigationHelper';
import {
  getCurrentAgeMetadata,
  getEntryById,
} from '#/redux/modules/accedoOne/selectors';
import {
  hasVideoEnded,
  getPlaylistIds,
  getPlayerVideoIndex,
  getPlayerContainerIndex,
} from '#/redux/modules/appPlayer/selectors';
import { setVideoPlaylist } from '#/redux/modules/appPlayer/actions';
import { getContainerShows } from '#/redux/modules/firestore/selectors';

import {
  getTopicQueryParams,
  getTopicData,
  getContainerData,
  getPlaylistQueryParams,
  getPlaylistData,
} from '#/redux/modules/brightcove/selectors';

import {
  getCustomDataShowsFavorites,
  getCustomDataTopicsFavorites,
  getCustomDataVideoFavorites,
} from '#/redux/modules/identity/selectors';

import {
  updateContainers,
  savePlaylistQuery,
  performPlaylistQuery,
} from '#/redux/modules/brightcove/actions';
import { getCurrentProfile } from '#/redux/modules/onboarding/selectors';

import styles from './playerMoreContent.scss';

import {
  containersTypeAlias,
  mainPageNames,
  playerConstants,
  videoAnalytics,
} from '#/config/constants';
import { isWorkstation } from '#/utils/componentStyleConfig';

const { CONTROL, CAROUSEL_CONTAINER, TRANSPORT_CONTROLS, CONTAINER } = PLAYER;

const {
  CONTAINER_SHOWS,
  CONTAINER_TOPICS,
  CONTAINER_OVP_PLAYLIST,
  TVCONTAINER_FAV_SHOWS,
  TVCONTAINER_FAV_TOPICS,
  TVCONTAINER_FAV_VIDEOS,
  CONTAINER_RECENTLY_WATCHED,
} = containersTypeAlias;

const navMap = {
  CONTAINER: {
    id: CONTROL,
    nextup: TRANSPORT_CONTROLS,
    parent: CONTAINER,
    forwardFocus: CAROUSEL_CONTAINER,
    useLastFocus: false,
  },
  ...getVerticalNav([CAROUSEL_CONTAINER], {
    parent: CONTROL,
  }),
};

const navIds = getVerticalNav([playerConstants.UP_NEXT_CAROUSEL_ID], {
  parent: CAROUSEL_CONTAINER,
});

const tabSchema = [{ label: 'Up Next', id: 0 }];

const hasChanges = function(oldArray, newArray) {
  return !(JSON.stringify(oldArray) === JSON.stringify(newArray));
};

const PlayerMoreContent = ({
  history,
  fromHome = false,
  onSwimlaneDataChange,
  location: { search },
}) => {
  const dispatch = useDispatch();
  const currentProfile = useSelector(getCurrentProfile);
  const onVideoEndFlag = useSelector(hasVideoEnded);
  const collection = new URLSearchParams(search).get('collectionid');
  const playlistIds = useSelector(getPlaylistIds);
  const getPlaylistId = collection ? [collection] : playlistIds;
  const videoIndex = useSelector(getPlayerVideoIndex);
  const [cachedItems, setCachedItems] = useState([]);
  const [cachedBcItems, setCachedBcItems] = useState([]);
  const bcContainerItems = useSelector(
    state => getContainerData(state, getPlaylistId),
    _isEqual,
  );

  const videoContainerIndex = useSelector(getPlayerContainerIndex);
  const selectedAgeMetadata = useSelector(getCurrentAgeMetadata);

  // Use the entry id to identify RW swimlane
  const rwEntry =
    useSelector(state => getEntryById(state, playlistIds?.[0])) || {};

  const containerAlias = useMemo(() => {
    if (rwEntry?._meta?.typeAlias === CONTAINER_RECENTLY_WATCHED) {
      return CONTAINER_RECENTLY_WATCHED;
    }
    const currentPath = window.location?.pathname?.split('/').slice(-1)[0];
    return currentPath === mainPageNames.FAVORITES && !isWorkstation()
      ? TVCONTAINER_FAV_VIDEOS
      : CONTAINER_OVP_PLAYLIST;
  }, [getPlaylistId?.[0]]);

  let queryParams = {};
  let items = [];
  let queryData = {};
  let containerItems = [];

  const type = containerAlias;
  const id =
    bcContainerItems?.[getPlaylistId?.[0]]?.playlistId || getPlaylistId?.[0];
  switch (type) {
    case CONTAINER_OVP_PLAYLIST:
      queryParams = useSelector(state => getPlaylistQueryParams(state));
      queryData = useSelector(state => getPlaylistData(state));
      items = useSelector(state => getContainerData(state, [`${id}-upnext`]))[
        `${id}-upnext`
      ];
      break;
    case CONTAINER_TOPICS:
      queryParams = useSelector(state => getTopicQueryParams(state));
      queryData = useSelector(state => getTopicData(state));
      containerItems =
        useSelector(state =>
          getContainerData(state, [queryData?.containerId]),
        ) || {};
      items =
        containerItems?.hasData && queryData?.containerId
          ? containerItems[queryData?.containerId]
          : [];
      break;
    case CONTAINER_RECENTLY_WATCHED:
      // queries are not used by recently watched swimlane
      // but react will complain for calling less hooks if removed
      queryParams = useSelector(state => getPlaylistQueryParams(state));
      queryData = useSelector(state => getPlaylistData(state));

      items = useSelector(state =>
        getContainerData(state, [getPlaylistId?.[0]]),
      )[getPlaylistId?.[0]];
      break;
    case CONTAINER_SHOWS:
      ({ items } = useSelector(getContainerShows));
      break;
    case TVCONTAINER_FAV_SHOWS:
      items = useSelector(getCustomDataShowsFavorites);
      break;
    case TVCONTAINER_FAV_TOPICS:
      items = useSelector(getCustomDataTopicsFavorites);
      break;
    case TVCONTAINER_FAV_VIDEOS:
      items = useSelector(getCustomDataVideoFavorites);
      break;
    default:
      break;
  }

  useEffect(() => {
    const playlistId = id;
    if (queryParams.playlistId !== playlistId) {
      dispatch(
        savePlaylistQuery({
          ...queryParams,
          id,
          containerId: `${playlistId}-upnext`,
          playlistId,
          limit: queryData.limit,
          offset: 0,
        }),
      );
      return;
    }

    if (queryParams.playlistId && queryParams.playlistId === playlistId) {
      dispatch(updateContainers({ queries: [queryParams] }));
    }
  }, [playlistIds, queryParams]);

  useEffect(() => {
    if (hasChanges(items, cachedItems)) {
      setCachedItems(items);
    }
  }, [items]);

  useEffect(() => {
    if (hasChanges(bcContainerItems, cachedBcItems)) {
      setCachedBcItems(bcContainerItems);
    }
  }, [bcContainerItems]);

  // RW updates the order of the playlist dynamically which
  // causes inconsistencies on the up next swimlane
  // To workaround this, save in memory the first order of items
  const initialItems = useMemo(
    () => (type === CONTAINER_RECENTLY_WATCHED ? items.slice(0) : []),
    [type],
  );
  let swimlaneData;
  switch (type) {
    case CONTAINER_RECENTLY_WATCHED: {
      swimlaneData = useMemo(() => {
        const data = [];
        const callback = onVideoTileClick(getPlaylistId, dispatch, fromHome);

        if (!initialItems || initialItems.length < 1) {
          return data;
        }

        const assets = initialItems;
        const upNextItemCount = assets.length;

        // Start from the the second video on the playlist
        let startingVideoIndex = videoIndex + 1;
        for (
          let playlistIdIndex = 0;
          playlistIdIndex < getPlaylistId.length;
          playlistIdIndex += 1
        ) {
          const currentContainerIndex =
            (playlistIdIndex + videoContainerIndex) % getPlaylistId.length;

          for (let i = startingVideoIndex; i < assets?.length; i += 1) {
            const item = { ...assets?.[i] }; // copy by reference to not affect other click handlers based on item for BG player
            item.onClick = callback(currentContainerIndex)(item, i);

            data.push(item);
            if (data.length === upNextItemCount) {
              break;
            }
          }

          if (data.length === upNextItemCount) {
            break;
          }
          startingVideoIndex = 0;
        }

        // If looping after the current video index and other containers is not enough
        // append videos before current index in same container
        if (data.length < upNextItemCount) {
          for (
            let currentVideoIndex = 0;
            currentVideoIndex <= videoIndex - 1;
            currentVideoIndex += 1
          ) {
            const item = {
              ...assets?.[currentVideoIndex],
            }; // copy by reference to not affect other click handlers based on item for BG player
            item.onClick = callback(videoContainerIndex)(
              item,
              currentVideoIndex,
            );

            data.push(item);
            if (data.length === upNextItemCount) {
              break;
            }
          }
        }

        return data;
      }, [initialItems, videoContainerIndex, videoIndex]);
      break;
    }
    default: {
      swimlaneData = useMemo(() => {
        const data = [];
        const callback = onVideoTileClick(getPlaylistId, dispatch, fromHome);

        const contItems = bcContainerItems?.[getPlaylistId?.[0]];
        if (
          (!items || items.length < 1) &&
          (!contItems || contItems.length < 1)
        ) {
          return data;
        }

        const assets = items?.length > 1 ? items : contItems;

        // Loop through videos in containers starting after the current video index
        let startingVideoIndex = videoIndex + 1;
        for (
          let playlistIdIndex = 0;
          playlistIdIndex < getPlaylistId.length;
          playlistIdIndex += 1
        ) {
          const currentContainerIndex =
            (playlistIdIndex + videoContainerIndex) % getPlaylistId.length;

          for (let i = startingVideoIndex; i < assets?.length; i += 1) {
            const item = { ...assets?.[i] }; // copy by reference to not affect other click handlers based on item for BG player
            item.onClick = callback(currentContainerIndex)(item, i);

            data.push(item);
          }

          startingVideoIndex = 0;
        }

        // If looping after the current video index and other containers is not enough
        // append videos before current index in same container
        for (
          let currentVideoIndex = 0;
          currentVideoIndex <= videoIndex - 1;
          currentVideoIndex += 1
        ) {
          const item = {
            ...assets?.[currentVideoIndex],
          }; // copy by reference to not affect other click handlers based on item for BG player
          item.onClick = callback(videoContainerIndex)(item, currentVideoIndex);

          data.push(item);
        }

        return data;
      }, [cachedItems, cachedBcItems, videoContainerIndex, videoIndex]);
      break;
    }
  }

  // Effect to trigger next content on video end
  useEffect(() => {
    const findNextUnblockedVideo = arr => {
      const res = arr.findIndex(vid =>
        currentProfile.videosBlocked.every(
          x => x.reference_id !== vid.reference_id,
        ),
      );

      return res;
    };
    const findNextUnblockedShow = arr => {
      const res = arr.findIndex(vid =>
        currentProfile.showsBlocked.every(
          x => x.id !== vid.custom_fields.show_id,
        ),
      );

      return res;
    };
    if (onVideoEndFlag) {
      // go back if no other up next content, otherwise play next video
      swimlaneData[0]
        ? currentProfile?.videosBlocked?.find(
            video => video.reference_id === swimlaneData[0].reference_id,
          )
          ? swimlaneData[findNextUnblockedVideo(swimlaneData)].onClick(
              videoAnalytics.initiation.AUTO,
            )
          : currentProfile?.showsBlocked?.find(
              show => show?.id === swimlaneData[0]?.custom_fields.show_id,
            )
          ? swimlaneData[findNextUnblockedShow(swimlaneData)].onClick(
              videoAnalytics.initiation.AUTO,
            )
          : swimlaneData[0].onClick(videoAnalytics.initiation.AUTO)
        : history.goBack();
    }
  }, [onVideoEndFlag, swimlaneData]);

  useEffect(() => {
    if (onVideoEndFlag && !isWorkstation()) {
      swimlaneData[0]?.onClick(videoAnalytics.initiation.AUTO);
    }
  }, [onVideoEndFlag]);

  useEffect(() => {
    onSwimlaneDataChange(swimlaneData);

    if (swimlaneData?.length > 0) {
      navMap[CAROUSEL_CONTAINER].forwardFocus =
        playerConstants.UP_NEXT_CAROUSEL_ID;
    }
  }, [swimlaneData]);

  if (collection) {
    queryData = useSelector(state => getPlaylistData(state));
    queryParams = useSelector(state => getPlaylistQueryParams(state));

    useEffect(() => {
      if (!queryParams.creator_id && queryParams.playlistId !== collection) {
        const queryObj = { ...queryParams };

        queryObj.limit = queryData.limit;
        queryObj.offset = 0;
        queryObj.playlistId = collection;
        queryObj.id = collection;
        dispatch(savePlaylistQuery(queryObj));
        dispatch(setVideoPlaylist([collection]));
      } else if (queryParams.playlistId === collection) {
        dispatch(performPlaylistQuery([queryParams]));
        dispatch(setVideoPlaylist([collection]));
      }
    }, [queryParams]);
  }

  return swimlaneData?.length ? (
    <Page autoFocus={false} nav={navMap.CONTAINER}>
      <FocusDiv nav={navMap[CAROUSEL_CONTAINER]}>
        <TabView
          className={classNames(styles.tabs, {
            [styles.isTv]: !isWorkstation(),
          })}
          tabsSchema={tabSchema}
          initialTabId={tabSchema[0].id}
        />
        <PlaylistSwimlaneFactory
          className={styles.swimlane}
          nav={navIds[playerConstants.UP_NEXT_CAROUSEL_ID]}
          videos={swimlaneData}
          alwaysVisible
          enableSeeAll={false}
          ageTheme={selectedAgeMetadata}
          fromHome
          useLastFocus={false}
          upnext
          enableAllUpNextVideo
        />
      </FocusDiv>
    </Page>
  ) : null;
};

PlayerMoreContent.propTypes = {
  history: PropTypes.object,
  fromHome: PropTypes.bool,
  onSwimlaneDataChange: PropTypes.func,
  location: PropTypes.any,
};

export default memo(withRouter(PlayerMoreContent), _isEqual);
